import { Modal, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { UOM_VALUES, type TaskQuantityRow, type Uom } from '../manager/types';
import { formatScope, parseScopeInput } from './scopeQtyFormat';

type Props = {
  taskKey: string;
  isSummary: boolean;
  canUpdate: boolean;
  quantity: TaskQuantityRow | null;
  /** Phase 2: a pending DM scope-change request exists for this task. */
  hasPendingProposal?: boolean;
  onSave: (args: { taskKey: string; uom: Uom; scopeQty: number }) => Promise<unknown>;
  onDelete: (taskKey: string) => Promise<unknown>;
};

function apiErrorMessage(e: unknown, fallback: string): string {
  if (typeof e === 'object' && e !== null) {
    const resp = (e as { response?: { data?: { message?: string } } }).response;
    if (resp?.data?.message) return resp.data.message;
  }
  return fallback;
}

/** Inline PMO editor for the task's total scope quantity (quantitative iff a master exists).
 *  Summary rows render nothing — scope is leaf-only (mixed UOMs cannot roll up). */
export function ScopeQtyCell({
  taskKey,
  isSummary,
  canUpdate,
  quantity,
  hasPendingProposal = false,
  onSave,
  onDelete,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [qtyText, setQtyText] = useState('');
  const [uom, setUom] = useState<Uom>('nos');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const seededFrom = useRef<string | null>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  // Re-seed the editor if the master changes underneath an open edit (e.g. a
  // concurrent PMO save landing via query invalidation) — last write should not
  // silently clobber a value the editor never saw.
  useEffect(() => {
    if (!editing) return;
    const identity = quantity ? `${quantity.scopeQty}|${quantity.uom}` : 'none';
    if (seededFrom.current === null) {
      seededFrom.current = identity;
      return;
    }
    if (seededFrom.current !== identity) {
      seededFrom.current = identity;
      setQtyText(quantity ? String(quantity.scopeQty) : '');
      setUom(quantity?.uom ?? 'nos');
      message.info('Scope was updated elsewhere — editor refreshed with the latest value');
    }
  }, [editing, quantity]);

  if (isSummary) return <span className="text-[#7C8A82]">&nbsp;</span>;

  const beginEdit = () => {
    setQtyText(quantity ? String(quantity.scopeQty) : '');
    setUom(quantity?.uom ?? 'nos');
    seededFrom.current = quantity ? `${quantity.scopeQty}|${quantity.uom}` : 'none';
    setEditing(true);
  };

  const commit = async () => {
    const n = parseScopeInput(qtyText);
    if (n === null) {
      message.warning('Scope must be a positive number');
      return;
    }
    setBusy(true);
    try {
      await onSave({ taskKey, uom, scopeQty: n });
      seededFrom.current = null;
      setEditing(false);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Could not save scope'));
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    if (!quantity) return;
    Modal.confirm({
      title: 'Remove scope?',
      content: `Remove scope (${formatScope(quantity)})? The task becomes non-quantitative.`,
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusy(true);
        try {
          await onDelete(taskKey);
          seededFrom.current = null;
          setEditing(false);
        } catch (e) {
          message.error(apiErrorMessage(e, 'Could not remove scope'));
        } finally {
          setBusy(false);
        }
      },
    });
  };

  if (editing) {
    return (
      <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="number"
          min={0}
          step="any"
          value={qtyText}
          disabled={busy}
          onChange={(e) => setQtyText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void commit();
            if (e.key === 'Escape') { seededFrom.current = null; setEditing(false); }
          }}
          aria-label={`Scope quantity for task ${taskKey}`}
          className="w-full min-w-[52px] rounded-md border border-[rgba(11,59,46,0.16)] bg-white px-1 py-0.5 text-right font-mono text-[11px] outline-none focus:border-[#339274]"
        />
        <select
          value={uom}
          disabled={busy}
          onChange={(e) => setUom(e.target.value as Uom)}
          aria-label={`Scope unit for task ${taskKey}`}
          className="w-[64px] rounded-md border border-[rgba(11,59,46,0.16)] bg-white px-1 py-0.5 text-[11px] outline-none focus:border-[#339274]"
        >
          {UOM_VALUES.map((u) => (
            <option key={u} value={u}>{u}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void commit()}
          disabled={busy}
          title="Save scope"
          aria-label="Save scope"
          className="shrink-0 rounded px-1 text-[11px] font-bold text-[#339274] hover:bg-[rgba(51,146,116,0.12)] disabled:opacity-40"
        >
          ✓
        </button>
        {quantity ? (
          <button
            type="button"
            onClick={() => clear()}
            disabled={busy}
            title="Remove scope (task becomes non-quantitative)"
            aria-label="Remove scope"
            className="shrink-0 rounded px-1 text-[11px] font-bold text-[#B4232A] hover:bg-[rgba(180,35,42,0.10)] disabled:opacity-40"
          >
            ✕
          </button>
        ) : null}
      </span>
    );
  }

  if (quantity) {
    return (
      <span
        className="flex items-center justify-end gap-1"
        title={
          quantity.derivedPercentComplete != null
            ? `${formatScope(quantity)} · ${quantity.derivedPercentComplete}% complete (field-derived)`
            : formatScope(quantity)
        }
      >
        {hasPendingProposal ? (
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#C9A961]"
            title="A DM scope-change proposal is pending"
          />
        ) : null}
        <span className="font-mono text-[11px] tabular-nums text-[#2A1F17]">{formatScope(quantity)}</span>
        {canUpdate ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); beginEdit(); }}
            title="Edit scope"
            aria-label="Edit scope"
            className="shrink-0 rounded px-1 text-[10px] text-[#7C8A82] hover:bg-[rgba(11,59,46,0.08)] hover:text-[#0B3B2E]"
          >
            ✎
          </button>
        ) : null}
      </span>
    );
  }

  if (canUpdate) {
    return (
      <span className="flex items-center justify-end gap-1">
        {hasPendingProposal ? (
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#C9A961]"
            title="A DM scope-change proposal is pending"
          />
        ) : null}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); beginEdit(); }}
          className="rounded px-1 py-0.5 text-[10.5px] font-semibold text-[#339274] hover:bg-[rgba(51,146,116,0.10)]"
          title="Set total scope — marks this task as quantitative"
        >
          + Add scope
        </button>
      </span>
    );
  }

  return (
    <span className="flex items-center justify-end gap-1">
      {hasPendingProposal ? (
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#C9A961]"
          title="A DM scope-change proposal is pending"
        />
      ) : null}
      <span className="text-[#7C8A82]">—</span>
    </span>
  );
}
